import { Button, CircularProgress, Skeleton, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { FC, ReactNode } from 'react';
import { Suspense, useCallback } from 'react';
import {
  ConfirmationDialog,
  GridErrorBoundaryFallback,
  SelectApplications,
  SelectCallToAction,
  SelectPlatform,
  SuspenseBoundary,
  TextField,
} from 'components/common';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import SelectCreativeTexts from './SelectCreativeTexts';
import { isEmpty } from 'lodash-es';
import DeleteFlow from './DeleteFlow';
import { useEnqueueSnackbar, useModal } from 'hooks';
import { useAddFlow, useUpdateFlow } from '../api';
import { addFlowFormValidator } from '../validation';
import type { z } from 'zod';
import { useNavigate } from 'react-router';
import { usePermissionGuard } from 'features/login';
import type { FlowDetails } from 'features/cabinet/tiktok/types';
import { NoAccessField } from 'components/layout';

type FormData = z.infer<typeof addFlowFormValidator.schema>;

interface FlowFormProps {
  data?: FlowDetails;
}

const SuspenseErrorBoundary: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <GlobalErrorBoundary FallbackComponent={GridErrorBoundaryFallback}>
      <Suspense
        fallback={
          <Skeleton
            height={40}
            sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
          />
        }
      >
        {children}
      </Suspense>
    </GlobalErrorBoundary>
  );
};

const ControlCreativeText: FC<{ flow?: FlowDetails }> = ({ flow }) => {
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
      >
        <SuspenseErrorBoundary>
          <SelectCreativeTexts
            name={'defaultCreativeTextId'}
            inputLabel={'Default creative text'}
            placeholder={'Value'}
            asAutocomplete
            flow={flow}
            required
          />
        </SuspenseErrorBoundary>
      </Box>
    </Box>
  );
};

const FlowForm: FC<FlowFormProps> = ({ data }) => {
  const { accessToDeleteTiktokflow, accessToViewTiktoktext } =
    usePermissionGuard();
  const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();
  const { open, close, isOpen } = useModal();
  const isEditMode = !isEmpty(data);

  const addFlow = useAddFlow({
    config: {
      onSuccess: () => {
        onShowAlert('Flow added successfully');
        navigate('/cabinet/tiktok/flows');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    },
  });

  const updateFlow = useUpdateFlow({
    config: {
      onSuccess: () => {
        onShowAlert('Flow updated successfully');
        navigate('/cabinet/tiktok/flows');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    },
  });

  const formMethods = useForm<FormData>({
    resolver: addFlowFormValidator.resolver,
    defaultValues: {
      flowId: data?.id,
      flowName: data?.name ?? '',
      app: data?.app ?? '',
      platform: data?.platform ?? '',
      url: data?.url ?? '',
      callToAction: data?.call_to_action,
      defaultCreativeTextId: data?.default_text?.id,
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = formMethods;

  const onSubmit = useCallback(
    (data: FormData) => {
      const payload = {
        name: data?.flowName,
        app: data?.app,
        platform: data?.platform,
        url: data?.url,
        call_to_action: data.callToAction,
        default_text_id: data.defaultCreativeTextId,
      };

      if (isEditMode) {
        updateFlow.mutate({ ...payload, flow_id: Number(data.flowId) });
      } else {
        addFlow.mutate(payload);
      }
    },
    [addFlow, updateFlow, isEditMode],
  );

  const isPending = isEditMode ? updateFlow.isPending : addFlow.isPending;

  return (
    <>
      <FormProvider {...formMethods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            maxWidth: '40%',
            alignItems: 'start',
            justifyContent: 'start',
          }}
        >
          <Typography variant={'subtitle1'}>
            {isEditMode ? 'Edit flow' : 'Add flow'}
          </Typography>
          <TextField
            name={'flowName'}
            inputLabel={'Flow Name'}
            placeholder={'Value'}
            inputProps={{ autoFocus: true }}
            required
          />
          <SelectApplications required name={'app'} />
          <SelectPlatform required name={'platform'} />
          <SelectCallToAction required name={'callToAction'} />
          <TextField
            name={'url'}
            inputLabel={'URL'}
            placeholder={'Value'}
            required
          />
          {accessToViewTiktoktext ? (
            <SuspenseBoundary>
              <ControlCreativeText flow={data} />
            </SuspenseBoundary>
          ) : (
            <NoAccessField text={'No access to view creative texts'} />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              startIcon={
                isPending && <CircularProgress color={'inherit'} size={18} />
              }
              onClick={() => handleSubmit(onSubmit)()}
              variant={'contained'}
              disabled={isPending || !isValid}
            >
              {isEditMode ? 'Update flow' : 'Save flow'}
            </Button>
            {isEditMode && data && (
              <>
                <Button
                  variant={'outlined'}
                  color={'error'}
                  onClick={open}
                  disabled={isPending || !accessToDeleteTiktokflow}
                >
                  Delete
                </Button>
                {accessToDeleteTiktokflow && (
                  <DeleteFlow
                    isOpen={isOpen}
                    close={close}
                    id={data.id}
                    name={data.name}
                    open={open}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
        <ConfirmationDialog
          isUnblock={addFlow.isSuccess || updateFlow.isSuccess}
        />
      </FormProvider>
    </>
  );
};

export default FlowForm;
